import CheckIcon from "@mui/icons-material/Check";
import { styled } from "@mui/material/styles";
import { appThemeColors } from "styles/appTheme";
import { appThemeFonts } from "styles/appThemeFonts";
import { appThemeRadius } from "styles/appThemeTokens";

const uiFont = appThemeFonts.family.ui;

export const CheckboxSelectTrigger = styled("button", {
  shouldForwardProp: (prop) => prop !== "$open" && prop !== "$error",
})(({ $open, $error }) => {
  const errorBorder = `1px solid ${appThemeColors.semantic.danger}`;
  const errorBorderOpen = `2px solid ${appThemeColors.semantic.danger}`;
  return {
    display: "flex",
    alignItems: "center",
    gap: 8,
    width: "100%",
    maxWidth: "100%",
    height: 32,
    padding: "0 9px",
    boxSizing: "border-box",
    background: appThemeColors.background.paper,
    border: $error
      ? $open
        ? errorBorderOpen
        : errorBorder
      : $open
        ? `2px solid ${appThemeColors.text.lightBlue}`
        : `1px solid ${appThemeColors.border.strong}`,
    borderRadius: appThemeRadius.md,
    cursor: "pointer",
    fontSize: "12.5px",
    fontFamily: uiFont,
    minWidth: 0,
    "&:disabled": {
      cursor: "not-allowed",
      opacity: 0.65,
    },
    "&:focus-visible": {
      outline: "2px solid #0B5FA5",
      outlineOffset: "2px",
    },
  };
});

export const CheckboxSelectTriggerLabel = styled("span", {
  shouldForwardProp: (prop) => prop !== "$empty" && prop !== "$error",
})(({ $empty, $error }) => ({
  flex: 1,
  minWidth: 0,
  textAlign: "left",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  color: $error
    ? appThemeColors.semantic.danger
    : $empty
      ? appThemeColors.text.faint
      : appThemeColors.text.primary,
}));

export const CheckboxSelectChevron = styled("span", {
  shouldForwardProp: (prop) => prop !== "$open",
})(({ $open }) => ({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "0 0 auto",
  color: appThemeColors.text.faint,
  fontSize: 16,
  lineHeight: 0,
  transform: $open ? "rotate(180deg)" : "none",
  transition: "transform 120ms ease",
}));

export const CheckboxSelectMenuHeader = styled("div")({
  padding: "8px 11px 5px",
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: appThemeColors.text.faint,
  fontFamily: uiFont,
});

export const CheckboxSelectMenuItem = styled("button")({
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  textAlign: "left",
  padding: "7px 10px",
  border: 0,
  background: "none",
  borderRadius: appThemeRadius.md,
  cursor: "pointer",
  fontSize: 13,
  fontFamily: uiFont,
  color: appThemeColors.text.primary,
  "&:hover": {
    backgroundColor: appThemeColors.background.kbd,
  },
  "&:disabled": {
    cursor: "not-allowed",
    opacity: 0.55,
  },
  "&:focus-visible": {
    outline: "2px solid #0B5FA5",
    outlineOffset: "2px",
  },
});

export const CheckboxSelectBox = styled("span", {
  shouldForwardProp: (prop) => prop !== "$checked",
})(({ $checked }) => ({
  width: 18,
  height: 18,
  flex: "0 0 auto",
  borderRadius: 5,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: $checked ? 0 : `1px solid ${appThemeColors.border.strong}`,
  background: $checked
    ? appThemeColors.text.lightBlue
    : appThemeColors.background.paper,
}));

export const CheckboxSelectItemLabel = styled("span")({
  minWidth: 0,
  fontWeight: 500,
});

export const CheckboxSelectCheckIcon = styled(CheckIcon)({
  fontSize: 12,
  color: appThemeColors.neutral.white,
});

export const CheckboxSelectErrorText = styled("div")({
  color: appThemeColors.semantic.danger,
  fontSize: 11,
  marginTop: 4,
});

export const checkboxSelectPopoverPaperSx = (width) => ({
  width: Math.max(238, width),
  mt: "5px",
  borderRadius: "14px",
  border: `1px solid ${appThemeColors.border.default}`,
  boxShadow: "0 20px 50px rgba(16,42,58,.22)",
  padding: "7px",
  overflow: "hidden",
});
